import { useEffect, useState } from "react";
import { MapPin, Phone, Store } from "lucide-react";
import { Alert, Modal, Skeleton } from "@/ui";
import { api } from "@/lib/api";
import { useLanguage } from "@/i18n/language";

/**
 * "How do I actually get my money?" — the question the withdraw screen cannot
 * answer on its own.
 *
 * A karigar who has never done this does not know that a code is read aloud to
 * a shopkeeper, that the shop pays out from its own balance, or that they may go
 * to any counter rather than only the one they registered at. None of that is
 * guessable from a form with an amount box, and getting it wrong means walking
 * to a shop and being turned away.
 *
 * The steps are numbered rather than written as a paragraph because this is
 * read on a phone, in a hurry, by someone who may be reading slowly.
 */
interface Counter {
  id: string;
  name: string;
  phone: string | null;
  address: string | null;
}

const STEPS: { hi: string; en: string }[] = [
  {
    hi: "जितने QCoin रिडीम करने हैं, वो लिखकर “कोड लें” दबाएं।",
    en: "Enter the QCoin you want to redeem and press “Get the code”.",
  },
  {
    hi: "6 अंकों का कोड आपकी स्क्रीन पर आएगा — WhatsApp पर भी। यह कभी खत्म नहीं होता।",
    en: "A 6-digit code appears on your screen, and on WhatsApp. It does not expire.",
  },
  {
    hi: "नीचे दी गई किसी भी दुकान पर जाएं।",
    en: "Go to any of the shops listed below.",
  },
  {
    hi: "दुकानदार को कोड बताएं। वे इसे अपने ऐप में डालेंगे।",
    en: "Read the code to the shopkeeper. They enter it in their app.",
  },
  {
    hi: "दुकानदार आपको नकद पैसे देंगे। बस।",
    en: "The shopkeeper pays you. That is all.",
  },
];

export function WithdrawHelp({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, pick } = useLanguage("hi");
  const [counters, setCounters] = useState<Counter[] | null>(null);
  const [failed, setFailed] = useState(false);

  /* Fetched when the sheet is first opened, not on page load — most visits to
   * the withdraw screen are from someone who already knows how this works. */
  useEffect(() => {
    if (!open || counters !== null) return;
    let cancelled = false;

    api
      .get<{ items: Counter[] }>("/org/me/shops")
      .then((res) => {
        if (!cancelled) setCounters(res.items);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [open, counters]);

  return (
    <Modal onClose={onClose} open={open} title={t("पैसे कैसे मिलेंगे?", "How to get your money")}>
      <div className="flex flex-col gap-5">
        <ol className="flex flex-col gap-3">
          {STEPS.map((step, i) => (
            <li className="flex gap-3" key={step.en}>
              <span
                aria-hidden="true"
                className="grid size-6 shrink-0 place-items-center rounded-full bg-brand-light font-title text-sm font-semibold text-brand-ink"
              >
                {i + 1}
              </span>
              <p className="min-w-0 pt-0.5 text-sm text-ink">{pick(step)}</p>
            </li>
          ))}
        </ol>

        <Alert kind="info" title={t("कोड कभी खत्म नहीं होता", "The code does not expire")}>
          {t(
            "जल्दी जाने की ज़रूरत नहीं — कोड तब तक चलेगा जब तक पैसे न मिल जाएं। मन बदले तो “रद्द करें” दबाकर अंक वापस ले लें।",
            "There is no rush. The code works until a shop pays you. Changed your mind? Press “Cancel” and your points come straight back.",
          )}
        </Alert>

        <div>
          <p className="mb-2 text-sm font-medium text-ink">
            {t(
              "इनमें से किसी भी दुकान पर जा सकते हैं — सिर्फ़ अपनी दुकान पर नहीं",
              "You can go to any of these shops — not only the one you registered at",
            )}
          </p>

          {failed ? (
            <Alert kind="danger">
              {t("दुकानों की सूची नहीं आ पाई।", "Could not load the shop list.")}
            </Alert>
          ) : counters === null ? (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : counters.length > 0 ? (
            <ul className="divide-y divide-rule rounded-lg border border-rule">
              {counters.map((c) => (
                <li className="flex flex-col gap-1 px-3 py-2.5" key={c.id}>
                  <p className="flex items-center gap-2 text-sm font-medium text-ink">
                    <Store aria-hidden="true" className="size-4 shrink-0 text-muted" />
                    {c.name}
                  </p>
                  {c.address ? (
                    <p className="flex items-start gap-2 text-xs text-muted">
                      <MapPin aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                      {c.address}
                    </p>
                  ) : null}
                  {c.phone ? (
                    /* A tel: link, because the next thing someone does with a
                       shop's number on a phone is ring it. */
                    <a
                      className="flex items-center gap-2 text-xs text-brand-ink underline"
                      href={`tel:${c.phone}`}
                    >
                      <Phone aria-hidden="true" className="size-3.5 shrink-0" />
                      {c.phone}
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <Alert kind="warning">
              {t(
                "अभी कोई दुकान नहीं जुड़ी है। अपने काउंटर से बात करें।",
                "No shops are listed yet. Speak to your counter.",
              )}
            </Alert>
          )}
        </div>
      </div>
    </Modal>
  );
}
